import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import { SetupFilterBar } from '../components';
import { useApi } from '../../../api/useApi';
import { CALIFORNIA_COUNTIES } from '../data/california-counties-svg-paths';
import { ZipcodeRecord, ZoneBadge, getZoneStyle } from '../data/location-shared';

interface SetupLocationLabProps {
  brand: BrandConfig;
  Icons: IconsType;
}

type LocationLabTab = 'countyOverview' | 'zoneAnalysis' | 'coverageSummary' | 'dataQuality';

// ── API Response Types ──

interface CountyDetail {
  county: string;
  zones: string[];
  isMixed: boolean;
  zipCount: number;
  cityCount: number;
  courtCount: number;
  areaNames: string[];
  zoneBreakdown: Record<string, { zips: number; cities: number }>;
  conflictingZipCount: number;
}

interface AreaInfo {
  name: string;
  zone: string;
  counties: string[];
}

interface SummaryResponse {
  totalRecords: number;
  totalCourts: number;
  uniqueCounties: number;
  uniqueCities: number;
  uniqueZips: number;
  mixedCounties: number;
  pureCounties: number;
  zoneZipCounts: Record<string, number>;
  courtZoneCounts: Record<string, number>;
  countyDetails: CountyDetail[];
  areas: AreaInfo[];
}

interface CrossZoneConflict {
  zipCode: number;
  city: string;
  county: string;
  zones: string[];
  areaNames: string[];
  aliases: string[];
}

interface DataQualityResponse {
  totalConflicts: number;
  conflicts: CrossZoneConflict[];
  conflictsByCounty: Record<string, number>;
  summary: {
    totalZips: number;
    cleanZips: number;
    cleanPct: number;
    affectedCounties: number;
  };
}

// ── Pagination Helpers ──

const PAGE_SIZE = 50;

// ── Component ──

const SetupLocationLab: React.FC<SetupLocationLabProps> = ({ brand, Icons }) => {
  const { get } = useApi();
  const [activeTab, setActiveTab] = useState<LocationLabTab>('countyOverview');
  const [isLoading, setIsLoading] = useState(true);

  // Data
  const [zipcodes, setZipcodes] = useState<ZipcodeRecord[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [dataQuality, setDataQuality] = useState<DataQualityResponse | null>(null);

  // ── Data Fetching ──

  const fetchZipcodes = useCallback(async () => {
    try {
      const data = await get<{ data: ZipcodeRecord[]; total: number }>('/api/admin/setup/location-lab/zipcodes');
      setZipcodes(data.data || []);
    } catch (error) {
      console.error('Failed to fetch zipcodes:', error);
    }
  }, [get]);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await get<SummaryResponse>('/api/admin/setup/location-lab/summary');
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, [get]);

  const fetchDataQuality = useCallback(async () => {
    try {
      const data = await get<DataQualityResponse>('/api/admin/setup/location-lab/data-quality');
      setDataQuality(data);
    } catch (error) {
      console.error('Failed to fetch data quality:', error);
    }
  }, [get]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([fetchZipcodes(), fetchSummary(), fetchDataQuality()]);
      setIsLoading(false);
    };
    load();
  }, [fetchZipcodes, fetchSummary, fetchDataQuality]);

  // ── Derived Data ──

  const countyOptions = useMemo(() => {
    const counties = [...new Set(zipcodes.map(r => r.County))].sort((a, b) => a.localeCompare(b));
    return counties.map(c => ({ value: c, label: c }));
  }, [zipcodes]);

  // ── Tab Navigation ──

  const tabs: { key: LocationLabTab; label: string }[] = [
    { key: 'countyOverview', label: 'County Overview' },
    { key: 'zoneAnalysis', label: 'Zone Analysis' },
    { key: 'coverageSummary', label: 'Coverage Summary' },
    { key: 'dataQuality', label: 'Data Quality' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div
        className="flex border-b mb-6"
        style={{ borderColor: brand.colors.border }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key ? '' : 'border-transparent'
            }`}
            style={{
              color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
              borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'countyOverview' && summary && (
        <CountyOverviewTab brand={brand} summary={summary} />
      )}

      {activeTab === 'zoneAnalysis' && summary && (
        <ZoneAnalysisTab brand={brand} summary={summary} />
      )}

      {activeTab === 'coverageSummary' && summary && (
        <CoverageSummaryTab brand={brand} summary={summary} />
      )}

      {activeTab === 'dataQuality' && dataQuality && (
        <DataQualityTab brand={brand} Icons={Icons} dataQuality={dataQuality} countyOptions={countyOptions} />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Pagination Controls
// ══════════════════════════════════════════════════════════════

interface PaginationProps {
  brand: BrandConfig;
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  totalFiltered: number;
}

const Pagination: React.FC<PaginationProps> = ({ brand, page, totalPages, onPageChange, totalFiltered }) => {
  if (totalPages <= 1) {
    return (
      <div className="mt-2 text-sm" style={{ color: brand.colors.textMuted }}>
        Showing {totalFiltered} rows
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between mt-3">
      <span className="text-sm" style={{ color: brand.colors.textMuted }}>
        Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalFiltered)} of {totalFiltered}
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => onPageChange(0)}
          disabled={page === 0}
          className="px-2 py-1 text-xs rounded border disabled:opacity-40"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          First
        </button>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          className="px-2 py-1 text-xs rounded border disabled:opacity-40"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          Prev
        </button>
        <span className="px-2 py-1 text-xs" style={{ color: brand.colors.text }}>
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          className="px-2 py-1 text-xs rounded border disabled:opacity-40"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          Next
        </button>
        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={page >= totalPages - 1}
          className="px-2 py-1 text-xs rounded border disabled:opacity-40"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          Last
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// County Overview
// ══════════════════════════════════════════════════════════════

interface CountyOverviewTabProps {
  brand: BrandConfig;
  summary: SummaryResponse;
}

const CountyOverviewTab: React.FC<CountyOverviewTabProps> = ({ brand, summary }) => {
  const mixedCounties = summary.countyDetails.filter(c => c.isMixed);
  const pureCounties = summary.countyDetails.filter(c => !c.isMixed);

  return (
    <div>
      {/* Stats Bar */}
      <div
        className="flex gap-4 mb-6 px-4 py-2 rounded-lg text-sm"
        style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
      >
        <span><strong>{summary.uniqueCounties}</strong> counties</span>
        <span>|</span>
        <span><strong>{summary.pureCounties}</strong> pure-zone</span>
        <span>|</span>
        <span><strong>{summary.mixedCounties}</strong> mixed-zone</span>
      </div>

      {/* Mixed-Zone Counties */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Mixed-Zone Counties
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {mixedCounties.map(county => {
          const totalZips = county.zipCount;
          const hasConflicts = county.conflictingZipCount > 0;
          return (
            <div
              key={county.county}
              className="rounded-lg border-2 p-4"
              style={{
                borderColor: hasConflicts ? '#EF4444' : '#F59E0B',
                backgroundColor: hasConflicts ? '#FEF2F2' : '#FFFBEB',
              }}
            >
              <h4 className="font-semibold text-base mb-2" style={{ color: brand.colors.text }}>
                {county.county}
              </h4>
              <div className="text-sm mb-3" style={{ color: brand.colors.textLight }}>
                {county.zipCount} zips &middot; {county.cityCount} cities &middot; {county.courtCount} courts
              </div>

              {/* Zone breakdown */}
              {Object.entries(county.zoneBreakdown).map(([zone, data]) => {
                const pct = totalZips > 0 ? (data.zips / totalZips) * 100 : 0;
                const style = getZoneStyle(zone);
                return (
                  <div key={zone} className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: style.text }}>{zone.replace(' Area', '')}</span>
                      <span style={{ color: brand.colors.textMuted }}>{data.zips} zips, {data.cities} cities</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: style.fill }}
                      />
                    </div>
                  </div>
                );
              })}

              {hasConflicts && (
                <div className="mt-1 text-xs" style={{ color: '#DC2626' }}>
                  ⚠ {county.conflictingZipCount} zips appear in multiple zones — see Data Quality tab
                </div>
              )}

              {/* Area names */}
              <div className="mt-2 text-xs" style={{ color: brand.colors.textMuted }}>
                Areas: {county.areaNames.join(', ')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pure-Zone Counties */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Pure-Zone Counties
      </h3>
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>County</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zone</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Zip Codes</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Cities</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Courts</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Area Name</th>
            </tr>
          </thead>
          <tbody>
            {pureCounties.map(county => (
              <tr
                key={county.county}
                className="border-t hover:bg-gray-50"
                style={{ borderColor: brand.colors.border }}
              >
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{county.county}</td>
                <td className="px-3 py-2"><ZoneBadge zone={county.zones[0]} /></td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>{county.zipCount}</td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>{county.cityCount}</td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>{county.courtCount}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{county.areaNames.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 3: Zone Analysis
// ══════════════════════════════════════════════════════════════

interface ZoneAnalysisTabProps {
  brand: BrandConfig;
  summary: SummaryResponse;
}

const ZoneAnalysisTab: React.FC<ZoneAnalysisTabProps> = ({ brand, summary }) => {
  const [hoveredCounty, setHoveredCounty] = useState<string | null>(null);

  const totalZips = summary.uniqueZips;
  const totalCourts = summary.totalCourts;

  // Build county → zone map for the SVG map
  const countyZoneMap = useMemo(() => {
    const map: Record<string, { zones: string[]; isMixed: boolean; zipCount: number }> = {};
    for (const c of summary.countyDetails) {
      map[c.county] = { zones: c.zones, isMixed: c.isMixed, zipCount: c.zipCount };
    }
    return map;
  }, [summary]);

  // Area hierarchy
  const areasByZone = useMemo(() => {
    const groups: Record<string, AreaInfo[]> = {
      'Remote Area': [],
      'Suburban Area': [],
      'Urban Area': [],
    };
    for (const area of summary.areas) {
      if (groups[area.zone]) {
        groups[area.zone].push(area);
      }
    }
    return groups;
  }, [summary]);

  return (
    <div>
      {/* Zip Code Distribution */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Zip Code Distribution by Zone
      </h3>
      <div className="mb-6">
        <StackedBar
          brand={brand}
          segments={[
            { label: 'Remote', value: summary.zoneZipCounts['Remote Area'] ?? 0, color: '#9CA3AF' },
            { label: 'Suburban', value: summary.zoneZipCounts['Suburban Area'] ?? 0, color: '#60A5FA' },
            { label: 'Urban', value: summary.zoneZipCounts['Urban Area'] ?? 0, color: '#34D399' },
          ]}
          total={totalZips}
        />
      </div>

      {/* Court Distribution */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Court Distribution by Zone
      </h3>
      <div className="mb-8">
        <StackedBar
          brand={brand}
          segments={[
            { label: 'Remote Court', value: summary.courtZoneCounts['Remote Court'] ?? 0, color: '#9CA3AF' },
            { label: 'Suburban Court', value: summary.courtZoneCounts['Suburban Court'] ?? 0, color: '#60A5FA' },
            { label: 'Urban Court', value: summary.courtZoneCounts['Urban Court'] ?? 0, color: '#34D399' },
            { label: 'eFiling', value: summary.courtZoneCounts['eFiling: California'] ?? 0, color: '#FBBF24' },
          ]}
          total={totalCourts}
        />
      </div>

      {/* County Zone Grid */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        County Zone Map
      </h3>
      <div className="mb-8">
        <CaliforniaCountyMap
          brand={brand}
          countyZoneMap={countyZoneMap}
          hoveredCounty={hoveredCounty}
          onHover={setHoveredCounty}
        />
      </div>

      {/* Area Hierarchy */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Area Hierarchy
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {(['Remote Area', 'Suburban Area', 'Urban Area'] as const).map(zone => {
          const style = getZoneStyle(zone);
          const areas = areasByZone[zone] ?? [];
          return (
            <div
              key={zone}
              className="rounded-lg border p-4"
              style={{ borderColor: brand.colors.border }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: style.fill }} />
                <span className="font-semibold" style={{ color: brand.colors.text }}>
                  {zone.replace(' Area', '')} ({areas.length} areas)
                </span>
              </div>
              <div className="space-y-1">
                {areas.map(area => (
                  <div key={area.name} className="text-sm" style={{ color: brand.colors.textLight }}>
                    {area.name}
                    <span className="text-xs ml-1" style={{ color: brand.colors.textMuted }}>
                      ({area.counties.length} {area.counties.length === 1 ? 'county' : 'counties'})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mixed County Zone Overlap Table */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Mixed-County Zone Overlap
      </h3>
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>County</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Remote Zips</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Suburban Zips</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Urban Zips</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Total Zips</th>
            </tr>
          </thead>
          <tbody>
            {summary.countyDetails.filter(c => c.isMixed).map(county => (
              <tr
                key={county.county}
                className="border-t"
                style={{ borderColor: brand.colors.border }}
              >
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{county.county}</td>
                <td className="px-3 py-2 text-center font-mono" style={{ color: brand.colors.text }}>
                  {county.zoneBreakdown['Remote Area']?.zips ?? 0}
                </td>
                <td className="px-3 py-2 text-center font-mono" style={{ color: brand.colors.text }}>
                  {county.zoneBreakdown['Suburban Area']?.zips ?? 0}
                </td>
                <td className="px-3 py-2 text-center font-mono" style={{ color: brand.colors.text }}>
                  {county.zoneBreakdown['Urban Area']?.zips ?? 0}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>
                  {county.zipCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Stacked Bar Component ──

interface StackedBarSegment {
  label: string;
  value: number;
  color: string;
}

interface StackedBarProps {
  brand: BrandConfig;
  segments: StackedBarSegment[];
  total: number;
}

const StackedBar: React.FC<StackedBarProps> = ({ brand, segments, total }) => {
  return (
    <div>
      <div className="flex h-8 rounded-lg overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
        {segments.map(seg => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={seg.label}
              className="flex items-center justify-center text-xs font-medium text-white"
              style={{ width: `${pct}%`, backgroundColor: seg.color, minWidth: pct > 3 ? undefined : '2px' }}
              title={`${seg.label}: ${seg.value} (${pct.toFixed(1)}%)`}
            >
              {pct > 8 && `${seg.value}`}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-2 text-xs" style={{ color: brand.colors.textLight }}>
        {segments.map(seg => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div key={seg.label} className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: seg.color }} />
              <span>{seg.label}: {seg.value} ({pct.toFixed(1)}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── California County Map (Grid-based) ──

interface CaliforniaCountyMapProps {
  brand: BrandConfig;
  countyZoneMap: Record<string, { zones: string[]; isMixed: boolean; zipCount: number }>;
  hoveredCounty: string | null;
  onHover: (county: string | null) => void;
}

const CaliforniaCountyMap: React.FC<CaliforniaCountyMapProps> = ({
  brand,
  countyZoneMap,
  hoveredCounty,
  onHover,
}) => {
  const getCountyFill = (zones: string[], isMixed: boolean): string => {
    if (isMixed) return '#F59E0B'; // amber for mixed
    const zone = zones[0];
    return getZoneStyle(zone).fill;
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-6">
        {/* SVG Map */}
        <div className="flex-1" style={{ maxWidth: 500 }}>
          <svg
            viewBox="0 0 800 900"
            width="100%"
            height="auto"
            style={{ backgroundColor: '#F9FAFB', borderRadius: 8 }}
          >
            {CALIFORNIA_COUNTIES.map((county) => {
              const data = countyZoneMap[county.name];
              const fill = data ? getCountyFill(data.zones, data.isMixed) : '#E5E7EB';
              const isHovered = hoveredCounty === county.name;
              return (
                <path
                  key={county.name}
                  d={county.path}
                  fill={fill}
                  stroke={isHovered ? '#1F2937' : '#FFF'}
                  strokeWidth={isHovered ? 2 : 0.5}
                  opacity={isHovered ? 1 : 0.85}
                  style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                  onMouseEnter={() => onHover(county.name)}
                  onMouseLeave={() => onHover(null)}
                >
                  <title>
                    {county.name}
                    {data ? `: ${data.zones.map(z => z.replace(' Area', '')).join(' + ')} (${data.zipCount} zips)` : ''}
                  </title>
                </path>
              );
            })}
          </svg>
        </div>

        {/* Hover tooltip + legend panel */}
        <div className="flex flex-col gap-4" style={{ minWidth: 200 }}>
          {/* Hover tooltip */}
          {hoveredCounty && countyZoneMap[hoveredCounty] ? (
            <div
              className="px-4 py-3 rounded-lg border"
              style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
            >
              <div className="font-semibold text-base mb-1" style={{ color: brand.colors.text }}>
                {hoveredCounty}
              </div>
              <div className="text-sm mb-1" style={{ color: brand.colors.textLight }}>
                {countyZoneMap[hoveredCounty].zones.map(z => z.replace(' Area', '')).join(' + ')}
              </div>
              <div className="text-sm" style={{ color: brand.colors.textMuted }}>
                {countyZoneMap[hoveredCounty].zipCount} zip codes
              </div>
            </div>
          ) : (
            <div
              className="px-4 py-3 rounded-lg border text-sm"
              style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight, color: brand.colors.textMuted }}
            >
              Hover over a county to see details
            </div>
          )}

          {/* Legend */}
          <div
            className="px-4 py-3 rounded-lg border"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="text-xs font-medium mb-2" style={{ color: brand.colors.textLight }}>Legend</div>
            <div className="space-y-2">
              {[
                { label: 'Remote', color: '#9CA3AF' },
                { label: 'Suburban', color: '#60A5FA' },
                { label: 'Urban', color: '#34D399' },
                { label: 'Mixed Zone', color: '#F59E0B' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: item.color }} />
                  <span style={{ color: brand.colors.text }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Coverage Summary
// ══════════════════════════════════════════════════════════════

interface CoverageSummaryTabProps {
  brand: BrandConfig;
  summary: SummaryResponse;
}

const CoverageSummaryTab: React.FC<CoverageSummaryTabProps> = ({ brand, summary }) => {
  const urbanRecords = summary.zoneZipCounts['Urban Area'] ?? 0;
  const urbanPct = summary.uniqueZips > 0 ? ((urbanRecords / summary.uniqueZips) * 100).toFixed(1) : '0';
  const urbanCounties = summary.countyDetails.filter(c => c.zones.includes('Urban Area')).length;

  return (
    <div>
      {/* Hero Narrative */}
      <div
        className="rounded-lg border p-6 mb-6"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
      >
        <p className="text-base leading-relaxed" style={{ color: brand.colors.text }}>
          The location dataset covers all <strong>{summary.uniqueCounties}</strong> California counties
          with <strong>{summary.uniqueZips.toLocaleString()}</strong> unique zip codes
          across <strong>{summary.uniqueCities.toLocaleString()}</strong> cities,
          plus <strong>{summary.totalCourts.toLocaleString()}</strong> court jurisdictions.
          Of the {summary.uniqueCounties} counties, <strong>{summary.pureCounties}</strong> are
          classified as a single zone, while <strong>{summary.mixedCounties}</strong> counties
          contain multiple zones. The data includes{' '}
          <strong>{summary.totalRecords.toLocaleString()}</strong> total records (including city aliases).
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          brand={brand}
          label="Counties"
          value={summary.uniqueCounties}
          sub={`${summary.pureCounties} pure + ${summary.mixedCounties} mixed`}
        />
        <MetricCard
          brand={brand}
          label="Zip Codes"
          value={summary.uniqueZips}
          sub={`${summary.uniqueCities.toLocaleString()} unique cities`}
        />
        <MetricCard
          brand={brand}
          label="Courts"
          value={summary.totalCourts}
          sub={`across all ${summary.uniqueCounties} counties`}
        />
      </div>

      {/* Zone Distribution Bars */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Zone Distribution
      </h3>

      <div className="mb-4">
        <div className="text-sm font-medium mb-1" style={{ color: brand.colors.textLight }}>Zip Codes</div>
        <StackedBar
          brand={brand}
          segments={[
            { label: 'Remote', value: summary.zoneZipCounts['Remote Area'] ?? 0, color: '#9CA3AF' },
            { label: 'Suburban', value: summary.zoneZipCounts['Suburban Area'] ?? 0, color: '#60A5FA' },
            { label: 'Urban', value: summary.zoneZipCounts['Urban Area'] ?? 0, color: '#34D399' },
          ]}
          total={summary.uniqueZips}
        />
      </div>

      <div className="mb-6">
        <div className="text-sm font-medium mb-1" style={{ color: brand.colors.textLight }}>Courts</div>
        <StackedBar
          brand={brand}
          segments={[
            { label: 'Remote Court', value: summary.courtZoneCounts['Remote Court'] ?? 0, color: '#9CA3AF' },
            { label: 'Suburban Court', value: summary.courtZoneCounts['Suburban Court'] ?? 0, color: '#60A5FA' },
            { label: 'Urban Court', value: summary.courtZoneCounts['Urban Court'] ?? 0, color: '#34D399' },
            { label: 'eFiling', value: summary.courtZoneCounts['eFiling: California'] ?? 0, color: '#FBBF24' },
          ]}
          total={summary.totalCourts}
        />
      </div>

      {/* Urban Concentration Callout */}
      <div
        className="rounded-lg border-2 p-4 mb-6"
        style={{ borderColor: '#34D399', backgroundColor: '#ECFDF5' }}
      >
        <h4 className="font-semibold mb-1" style={{ color: '#065F46' }}>
          Urban Concentration
        </h4>
        <p className="text-sm" style={{ color: '#047857' }}>
          Only <strong>{urbanCounties}</strong> counties contain urban zones,
          covering <strong>{urbanRecords}</strong> zip codes ({urbanPct}% of all zips).
          Urban zones are concentrated in the largest metro areas:
          Los Angeles, San Diego, Sacramento, and San Francisco.
        </p>
      </div>

      {/* Data Completeness */}
      <div
        className="rounded-lg border p-4"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
      >
        <h4 className="font-semibold mb-2" style={{ color: brand.colors.text }}>
          Data Completeness
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div style={{ color: brand.colors.textMuted }}>Total Records</div>
            <div className="font-mono font-semibold" style={{ color: brand.colors.text }}>
              {summary.totalRecords.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ color: brand.colors.textMuted }}>Unique Zip Codes</div>
            <div className="font-mono font-semibold" style={{ color: brand.colors.text }}>
              {summary.uniqueZips.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ color: brand.colors.textMuted }}>Unique Cities</div>
            <div className="font-mono font-semibold" style={{ color: brand.colors.text }}>
              {summary.uniqueCities.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ color: brand.colors.textMuted }}>Alias Entries</div>
            <div className="font-mono font-semibold" style={{ color: brand.colors.text }}>
              {(summary.totalRecords - summary.uniqueZips).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Metric Card ──

interface MetricCardProps {
  brand: BrandConfig;
  label: string;
  value: number;
  sub: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ brand, label, value, sub }) => (
  <div
    className="rounded-lg border p-4 text-center"
    style={{ borderColor: brand.colors.border }}
  >
    <div className="text-3xl font-bold mb-1" style={{ color: brand.colors.link }}>
      {value.toLocaleString()}
    </div>
    <div className="font-medium" style={{ color: brand.colors.text }}>{label}</div>
    <div className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>{sub}</div>
  </div>
);

// ══════════════════════════════════════════════════════════════
// Tab 7: Data Quality
// ══════════════════════════════════════════════════════════════

interface DataQualityTabProps {
  brand: BrandConfig;
  Icons: IconsType;
  dataQuality: DataQualityResponse;
  countyOptions: { value: string; label: string }[];
}

const DataQualityTab: React.FC<DataQualityTabProps> = ({ brand, Icons, dataQuality, countyOptions }) => {
  const [dqCountyFilter, setDqCountyFilter] = useState('all');
  const [dqPage, setDqPage] = useState(0);

  const filteredConflicts = useMemo(() => {
    if (dqCountyFilter === 'all') return dataQuality.conflicts;
    return dataQuality.conflicts.filter(c => c.county === dqCountyFilter);
  }, [dataQuality.conflicts, dqCountyFilter]);

  const paginatedConflicts = useMemo(() => {
    const start = dqPage * PAGE_SIZE;
    return filteredConflicts.slice(start, start + PAGE_SIZE);
  }, [filteredConflicts, dqPage]);

  const dqTotalPages = Math.ceil(filteredConflicts.length / PAGE_SIZE);

  // Only show counties that have conflicts
  const conflictCountyOptions = useMemo(() => {
    return countyOptions.filter(o => dataQuality.conflictsByCounty[o.value]);
  }, [countyOptions, dataQuality.conflictsByCounty]);

  return (
    <div>
      {/* A. Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          brand={brand}
          label="Total Conflicts"
          value={dataQuality.totalConflicts}
          sub={`zips assigned to multiple zones`}
        />
        <MetricCard
          brand={brand}
          label="Affected Counties"
          value={dataQuality.summary.affectedCounties}
          sub={`of ${dataQuality.summary.totalZips.toLocaleString()} total zips`}
        />
        <div
          className="rounded-lg border p-4 text-center"
          style={{ borderColor: brand.colors.border }}
        >
          <div className="text-3xl font-bold mb-1" style={{ color: '#059669' }}>
            {dataQuality.summary.cleanPct}%
          </div>
          <div className="font-medium" style={{ color: brand.colors.text }}>Clean Zips</div>
          <div className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>
            {dataQuality.summary.cleanZips.toLocaleString()} zips with single zone
          </div>
        </div>
      </div>

      {/* B. Per-County Conflict Summary */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Conflicts by County
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Object.entries(dataQuality.conflictsByCounty)
          .sort(([, a], [, b]) => b - a)
          .map(([county, count]) => {
            const countyConflicts = dataQuality.conflicts.filter(c => c.county === county);
            return (
              <div
                key={county}
                className="rounded-lg border-2 p-4"
                style={{ borderColor: '#EF4444', backgroundColor: '#FEF2F2' }}
              >
                <h4 className="font-semibold text-base mb-1" style={{ color: brand.colors.text }}>
                  {county}
                </h4>
                <div className="text-2xl font-bold mb-1" style={{ color: '#DC2626' }}>
                  {count}
                </div>
                <div className="text-sm" style={{ color: brand.colors.textLight }}>
                  conflicting zip codes
                </div>
                <div className="mt-2 text-xs" style={{ color: brand.colors.textMuted }}>
                  Zones involved: {[...new Set(countyConflicts.flatMap(c => c.zones))].map(z => z.replace(' Area', '')).join(', ')}
                </div>
              </div>
            );
          })}
      </div>

      {/* C. Conflict Detail Table */}
      <h3 className="text-lg font-semibold mb-3" style={{ color: brand.colors.text }}>
        Conflict Details
      </h3>

      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue=""
        onSearchChange={() => {}}
        searchPlaceholder=""
        categoryFilter={dqCountyFilter}
        categoryOptions={conflictCountyOptions}
        onCategoryChange={(v) => { setDqCountyFilter(v); setDqPage(0); }}
        statusFilter="all"
        statusOptions={[]}
        onStatusChange={() => {}}
      />

      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zip Code</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>City</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>County</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zones</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Area Names</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Aliases</th>
            </tr>
          </thead>
          <tbody>
            {paginatedConflicts.map((conflict) => (
              <tr
                key={`${conflict.city}|${conflict.zipCode}|${conflict.county}`}
                className="border-t hover:bg-gray-50"
                style={{ borderColor: brand.colors.border }}
              >
                <td className="px-3 py-2 font-mono" style={{ color: brand.colors.text }}>
                  {String(conflict.zipCode).padStart(5, '0')}
                </td>
                <td className="px-3 py-2" style={{ color: brand.colors.text }}>{conflict.city}</td>
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{conflict.county}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {conflict.zones.map(zone => (
                      <ZoneBadge key={zone} zone={zone} />
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>
                  {conflict.areaNames.join(', ')}
                </td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>
                  {conflict.aliases?.join(', ') ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        brand={brand}
        page={dqPage}
        totalPages={dqTotalPages}
        onPageChange={setDqPage}
        totalFiltered={filteredConflicts.length}
      />

      {/* D. Explanation Callout */}
      <div
        className="rounded-lg border-2 p-4 mt-6"
        style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}
      >
        <h4 className="font-semibold mb-2" style={{ color: '#92400E' }}>
          What are cross-zone conflicts?
        </h4>
        <p className="text-sm mb-2" style={{ color: '#78350F' }}>
          A cross-zone conflict occurs when the same zip code appears in more than one pricing zone
          within the dataset. For accurate pricing, each zip code should map to exactly one zone
          (Remote, Suburban, or Urban).
        </p>
        <p className="text-sm" style={{ color: '#78350F' }}>
          These conflicts need manual review to determine the correct zone assignment for each
          affected zip code. Until resolved, orders to these zip codes may receive inconsistent
          pricing depending on which record is matched first.
        </p>
      </div>
    </div>
  );
};

export default SetupLocationLab;
